import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import IconPicker from "components/IconPicker";
import { pick } from "lodash";
import { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import Field from "components/Field";
import { useIntl } from "react-intl";
import { IMenuItem, MenuItemRequest } from "services/KnowfullyAdminClient";
import { CommonFormProps, MenuItemTypeIds } from "types/common-types";
import SelectMenuItemType from "./SelectMenuItemType";
import SelectSku from "components/SelectSku";

const skuContentCatalogMyPortalUrl = (skuId?: string | null) =>
  `/app/catalogs/${skuId ?? "{skuId}"}`;

const { Form } = withTypes<MenuItemRequest>();

const useStyles = () => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

const MenuItemForm = ({
  entity: menuItem,
  submitLabel,
  onCancel,
  onSubmit,
}: CommonFormProps<IMenuItem, MenuItemRequest> &
  FormProps<MenuItemRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles();

  const init = useMemo(
    () => ({
      ...pick(menuItem ?? {}, [
        "name",
        "toolTip",
        "description",
        "url",
        "claim",
        "icon",
        "menuItemTypeId",
        "menuItemValues",
      ]),
    }),
    [menuItem]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values, form }) => (
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="name" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      required
                      label={t({ id: "nameLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="description">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "descriptionLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="toolTip">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "toolTipLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="url" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      required
                      disabled={
                        values.menuItemTypeId?.toLowerCase() ===
                        MenuItemTypeIds.SkuContent
                      }
                      label={t({ id: "urlLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="claim">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "claimLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="menuItemTypeId" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <SelectMenuItemType
                      {...input}
                      fullWidth
                      required
                      label={t({ id: "typeLabel" })}
                      textFieldProps={{
                        error: meta.error && meta.submitFailed,
                        helperText:
                          meta.error && meta.submitFailed && meta.error,
                      }}
                      onChange={(typeId) => {
                        input.onChange(typeId);
                        if (typeId?.toLowerCase() === MenuItemTypeIds.Url) {
                          form.change("menuItemValues", {});
                        }
                      }}
                    />
                  </Grid>
                )}
              </Field>

              {values.menuItemTypeId?.toLowerCase() ===
                MenuItemTypeIds.SkuContent && (
                <Field name="menuItemValues.skuId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <SelectSku
                        {...input}
                        fullWidth
                        required
                        hasSkuContents
                        label={t({ id: "skuLabel" })}
                        textFieldProps={{
                          error: meta.error && meta.submitFailed,
                          helperText:
                            meta.error && meta.submitFailed && meta.error,
                        }}
                        onChange={(id) => {
                          input.onChange(id);
                          form.change("url", skuContentCatalogMyPortalUrl(id));
                        }}
                      />
                    </Grid>
                  )}
                </Field>
              )}

              <Field name="icon">
                {({ input }) => (
                  <Grid item xs={12}>
                    <IconPicker {...input} />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            {submitting && <CircularProgress size={24} />}
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button type="submit" color="primary" disabled={submitting}>
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default MenuItemForm;
